/**
 * The proposal itself: the rate card, whole, with every arrangement explained.
 *
 * Split out of ProposalPage so it can be shown in two places without being written twice:
 *
 *   /proposal/:token          public, unauthenticated — the link in an email or a WhatsApp
 *                             message. Someone evaluating an offer should not have to
 *                             remember a password to look at it.
 *   inside the portal         a partner who is already signed in reads it in a dialog and
 *                             stays where they were. The standalone page had no way back to
 *                             the portal at all, which is a dead end at the exact moment they
 *                             have just said yes.
 *
 * Both shells render THIS. The accept action is passed in, because what should happen after
 * "accepted" differs: the page stays put and shows the banner, the dialog closes.
 */
import { Check, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';

const inr = (n) => '₹' + Math.round(Number(n) || 0).toLocaleString('en-IN');

/**
 * The card this partner was actually offered.
 *
 * The API sends the PUBLISHED card plus a `negotiated` diff of dotted paths, and until now the
 * page rendered only the first. A partner whose co-brand minimum had been cut to ₹10,000 was
 * shown ₹50,000 — the list price — on the document they then accepted. Showing the published
 * number is worse than showing nothing: it is wrong, and it is wrong on the artefact that
 * freezes their terms.
 *
 * So the diff is applied here and every table below reads the result. `negotiatedPaths` keeps
 * the set so each changed number can say so; backend/proposals.py:203 is explicit that the
 * partner is entitled to see which numbers are theirs specially.
 */
const applyNegotiated = (card, negotiated) => {
  if (!card) return card;
  const merged = JSON.parse(JSON.stringify(card));
  Object.entries(negotiated || {}).forEach(([path, value]) => {
    const parts = path.split('.');
    let node = merged;
    for (let i = 0; i < parts.length - 1; i += 1) {
      if (node[parts[i]] == null || typeof node[parts[i]] !== 'object') node[parts[i]] = {};
      node = node[parts[i]];
    }
    node[parts[parts.length - 1]] = value;
  });
  return merged;
};

/** A quiet marker on a number that was agreed for this partner and nobody else. */
const Negotiated = ({ on, path }) => (
  on.has(path)
    ? <span className="ml-2 align-middle text-[10px] uppercase tracking-wide font-medium
                       text-[#B45309] bg-[#FEF3C7] rounded px-1.5 py-0.5">agreed for you</span>
    : null
);

/** A rung is either a percentage or a flat per-client fee — the card carries both shapes. */
const rungLabel = (rate) => {
  if (rate == null) return '—';
  if (typeof rate === 'object') return `${inr(rate.flat)} per client`;
  return `${Math.round(Number(rate) * 1000) / 10}%`;
};

/**
 * The arrangements, in the order they ask more of a partner and pay more for it.
 *
 * `key` matches the commission table on the rate card. `reseller` has no commission column — it
 * is not a rate flavour, it is the one arrangement where we hand over the billing relationship,
 * so it reads the wallet discount ladder instead.
 */
const ARRANGEMENTS = [
  {
    key: 'affiliate',
    title: 'Affiliate',
    line: 'Send people our way.',
    body: 'You point someone at us with your link. We do everything else — the demo, the sale, '
        + 'the onboarding, the support. You are paid for the introduction and carry no delivery.',
    asks: 'A link, and an audience worth putting it in front of.',
  },
  {
    key: 'referral',
    title: 'Referral',
    line: 'Introduce a client and stay in the room.',
    body: 'You bring a client and help us get them live — you know their business, we run the '
        + 'platform. We bill the client directly and you earn every month for as long as they '
        + 'stay.',
    asks: 'A real relationship with the client, and a warm introduction rather than a list.',
  },
  {
    key: 'solution',
    title: 'Solution partner',
    line: 'Run your book on us.',
    body: 'You operate Expedify for your clients as part of what you already deliver. We bill '
        + 'the client, you earn on their spend every month, and the tier you sit on climbs with '
        + 'the size of the book you are running.',
    asks: 'The ability to actually operate the platform for a client, not just introduce one.',
  },
  {
    key: 'whitelabel',
    whitelabel: true,
    title: 'White label — your brand on the platform',
    line: 'Run Expedify as your own product.',
    body: 'Your clients sign in on your subdomain, with your logo, your colours and your name '
        + 'on the screen. Co-branding keeps a small "Powered by Expedify" line; full white '
        + 'label removes it. It sits on top of whichever arrangement you are on — it changes '
        + 'what your clients see, not how you are paid.',
    asks: 'A monthly commitment, below. It is the one arrangement with a floor, because we are '
        + 'carrying your brand rather than ours.',
  },
  {
    key: 'reseller',
    wallet: true,
    title: 'Reseller — you bill your clients',
    line: 'Buy credits at a discount and set your own price.',
    body: 'Instead of us paying you commission, you buy platform credits up front at a discount '
        + 'and bill your clients yourself. You own the invoice, the relationship and the margin '
        + '— whatever you charge above your cost is yours.',
    asks: 'Approval as a solution partner first, and the working capital to buy ahead.',
  },
];

const Section = ({ children }) => (
  <div className="bg-white rounded-2xl border border-[#E2E2DF] p-6 shadow-sm">{children}</div>
);

/** One arrangement: what it is, what it asks, and what it pays at every rung. */
const CommissionArrangement = ({ spec, card, negotiatedPaths }) => {
  const rungs = (card.commission || {})[spec.key] || {};
  // Thresholds are stored FLAT — {gold: 50000, platinum: 100000} — because a tier is a property
  // of the partner, not of the arrangement. This read them as tier_thresholds[arrangement],
  // which is always undefined, so every row on every proposal said "where everyone starts".
  const thresholds = card.tier_thresholds || {};
  const order = card.tier_order || ['silver', 'gold', 'platinum'];
  if (!Object.keys(rungs).length) return null;

  return (
    <Section>
      <h2 className="font-semibold text-[#1A1A1A]">{spec.title}</h2>
      <p className="text-sm font-medium text-[#1A1A1A] mt-1">{spec.line}</p>
      <p className="text-sm text-[#64748B] mt-2">{spec.body}</p>

      <table className="w-full mt-5 text-sm" data-testid={`ladder-${spec.key}`}>
        <thead>
          <tr className="text-xs uppercase tracking-wide text-[#64748B]">
            <th className="text-left font-normal pb-2">Tier</th>
            <th className="text-left font-normal pb-2">You earn</th>
            <th className="text-right font-normal pb-2">Reached at</th>
          </tr>
        </thead>
        <tbody>
          {order.map((tier) => (
            <tr key={tier} className="border-t border-[#E2E2DF]">
              <td className="py-3 capitalize font-medium text-[#1A1A1A]">{tier}</td>
              <td className="py-3 font-semibold text-[#1A1A1A]">
                {rungLabel(rungs[tier])}
                <Negotiated on={negotiatedPaths} path={`commission.${spec.key}.${tier}`} />
              </td>
              <td className="py-3 text-right text-[#64748B]">
                {thresholds[tier] != null
                  ? <>{inr(thresholds[tier])} earned in a quarter
                      <Negotiated on={negotiatedPaths} path={`tier_thresholds.${tier}`} /></>
                  : 'where everyone starts'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-[#64748B] mt-4">
        <span className="font-medium text-[#1A1A1A]">What it asks:</span> {spec.asks}
      </p>
    </Section>
  );
};

/**
 * White label: two levels, each with a monthly floor and a price.
 *
 * This section did not exist. The rate card has carried a `whitelabel` block all along and the
 * admin editor offers all four of its fields for negotiation — so an admin could agree a
 * white-label price with a partner who then had no way, anywhere, to read it. That is the
 * whole of what this fixes.
 *
 * A null rate is a question, not a zero (see rates.py on `whitelabel_rate`), so it is rendered
 * as "priced with you" rather than as free.
 */
const WhitelabelArrangement = ({ spec, card, negotiatedPaths }) => {
  const wl = card.whitelabel || {};
  if (wl.cobrand_min_monthly == null && wl.full_min_monthly == null) return null;

  const levels = [
    {
      name: 'Co-brand',
      note: 'Your brand, with a "Powered by Expedify" line.',
      min: wl.cobrand_min_monthly,
      rate: wl.cobrand_rate,
      minPath: 'whitelabel.cobrand_min_monthly',
      ratePath: 'whitelabel.cobrand_rate',
    },
    {
      name: 'Full white label',
      note: 'Your brand only. We are not named.',
      min: wl.full_min_monthly,
      rate: wl.full_rate,
      minPath: 'whitelabel.full_min_monthly',
      ratePath: 'whitelabel.full_rate',
    },
  ].filter((l) => l.min != null || l.rate != null);

  return (
    <Section>
      <h2 className="font-semibold text-[#1A1A1A]">{spec.title}</h2>
      <p className="text-sm font-medium text-[#1A1A1A] mt-1">{spec.line}</p>
      <p className="text-sm text-[#64748B] mt-2">{spec.body}</p>

      <table className="w-full mt-5 text-sm" data-testid="ladder-whitelabel">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-[#64748B]">
            <th className="text-left font-normal pb-2">Level</th>
            <th className="text-left font-normal pb-2">Minimum / month</th>
            <th className="text-right font-normal pb-2">Price</th>
          </tr>
        </thead>
        <tbody>
          {levels.map((l) => (
            <tr key={l.name} className="border-t border-[#E2E2DF]">
              <td className="py-3">
                <span className="font-medium text-[#1A1A1A]">{l.name}</span>
                <p className="text-xs text-[#64748B] mt-0.5">{l.note}</p>
              </td>
              <td className="py-3 font-semibold text-[#1A1A1A]">
                {l.min != null ? inr(l.min) : '—'}
                <Negotiated on={negotiatedPaths} path={l.minPath} />
              </td>
              <td className="py-3 text-right font-semibold text-[#1A1A1A]">
                {l.rate != null
                  ? <>{inr(l.rate)}<span className="font-normal text-[#64748B]"> / month</span></>
                  : <span className="font-normal text-[#64748B]">priced with you</span>}
                <Negotiated on={negotiatedPaths} path={l.ratePath} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="text-xs text-[#64748B] mt-4">
        <span className="font-medium text-[#1A1A1A]">What it asks:</span> {spec.asks}
      </p>
    </Section>
  );
};

/** The reseller arrangement, which reads the wallet discount ladder. */
const ResellerArrangement = ({ spec, card, eligible, negotiatedPaths }) => {
  const wallet = card.wallet || {};
  const order = card.tier_order || ['silver', 'gold', 'platinum'];
  const discounts = wallet.discount || {};

  // It read `wallet.ladder` — a purchase-size ladder of {from_face_value, discount} that no
  // rate card has ever carried. The array was always empty and this component always returned
  // null, so the entire reseller arrangement was invisible on every proposal ever sent. The
  // discount IS the tier now (see rates.py on wallet.discount), exactly like the commission
  // rate, so it reads the same way the commission tables do.
  if (!Object.keys(discounts).length) return null;

  return (
    <Section>
      <h2 className="font-semibold text-[#1A1A1A]">{spec.title}</h2>
      <p className="text-sm font-medium text-[#1A1A1A] mt-1">{spec.line}</p>
      <p className="text-sm text-[#64748B] mt-2">{spec.body}</p>

      <table className="w-full mt-5 text-sm" data-testid="ladder-reseller">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-[#64748B]">
            <th className="text-left font-normal pb-2">Tier</th>
            <th className="text-right font-normal pb-2">You buy credits at</th>
          </tr>
        </thead>
        <tbody>
          {order.filter((tier) => discounts[tier] != null).map((tier) => (
            <tr key={tier} className="border-t border-[#E2E2DF]">
              <td className="py-3 capitalize font-medium text-[#1A1A1A]">{tier}</td>
              <td className="py-3 text-right font-semibold text-[#1A1A1A]">
                {Math.round(Number(discounts[tier]) * 1000) / 10}% off
                <Negotiated on={negotiatedPaths} path={`wallet.discount.${tier}`} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="text-xs text-[#64748B] mt-4">
        Your discount follows your tier, the same way your commission rate does.
        Minimum purchase {inr(wallet.floor)}
        <Negotiated on={negotiatedPaths} path="wallet.floor" />
        {' · '}credits valid {wallet.expiry_months} months in the wallet
        {wallet.min_client_seed != null
          ? <> · each client needs {inr(wallet.min_client_seed)} every {wallet.client_expiry_months} months to stay live</>
          : null}
        .
      </p>
      <p className="text-xs text-[#64748B] mt-2">
        <span className="font-medium text-[#1A1A1A]">What it asks:</span> {spec.asks}
        {!eligible && ' Talk to us about this one — it is not open on your account yet.'}
      </p>
    </Section>
  );
};


/**
 * @param proposal   the public view from GET /api/proposals/:token
 * @param onAccept   async; the shell decides what happens afterwards
 * @param deciding   true while onAccept is in flight
 * @param acceptAtTop  put the accept box above the tables. The dialog does — a partner who
 *                     has already read this once should not scroll a rate card to say yes.
 */
export const ProposalContent = ({ proposal, onAccept, deciding, acceptAtTop = false }) => {
  const { status } = proposal;
  const decided = status === 'accepted' || status === 'declined';
  // Every table below reads the MERGED card, so a partner sees their own numbers rather than
  // the list price they were never offered.
  const negotiated = proposal.negotiated || {};
  const negotiatedPaths = new Set(Object.keys(negotiated));
  const card = applyNegotiated(proposal.rate_card, negotiated);

  const acceptBox = !decided && (
    <Section>
      <p className="text-sm text-[#64748B] mb-4">
        Accepting confirms these rates as the terms of your partnership and starts your setup.
        Nothing is charged now, and this is not the partnership agreement — that comes next,
        and you sign it.
      </p>
      <Button onClick={onAccept} disabled={deciding} size="lg" data-testid="accept-proposal">
        {deciding ? 'Recording…' : <>Accept these terms <ArrowRight className="w-4 h-4 ml-2" /></>}
      </Button>
    </Section>
  );

  return (
    <div className="space-y-6">
      {status === 'accepted' && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-2">
          <Check className="w-5 h-5 text-green-700" />
          <p className="text-sm text-green-900 font-medium">
            Accepted. We'll be in touch to get you set up.
          </p>
        </div>
      )}
      {status === 'declined' && (
        <div className="bg-[#F4F4F2] border border-[#E2E2DF] rounded-xl p-4">
          <p className="text-sm text-[#64748B]">This proposal was declined.</p>
        </div>
      )}

      {acceptAtTop && acceptBox}

      {negotiatedPaths.size > 0 && (
        <div className="bg-[#FEF9EC] border border-[#FDE68A] rounded-xl p-4">
          <p className="text-sm text-[#1A1A1A]">
            <strong>{negotiatedPaths.size} term{negotiatedPaths.size === 1 ? '' : 's'} on this
            proposal {negotiatedPaths.size === 1 ? 'was' : 'were'} agreed for you specifically.</strong>
          </p>
          <p className="text-xs text-[#64748B] mt-1">
            They are marked below, and the figures shown are yours — not the published card.
            Accepting freezes them.
          </p>
        </div>
      )}

      {card && ARRANGEMENTS.map((spec) => {
        if (spec.wallet) {
          return <ResellerArrangement key={spec.key} spec={spec} card={card}
                                      eligible={proposal.eligible_for_reseller}
                                      negotiatedPaths={negotiatedPaths} />;
        }
        if (spec.whitelabel) {
          return <WhitelabelArrangement key={spec.key} spec={spec} card={card}
                                        negotiatedPaths={negotiatedPaths} />;
        }
        return <CommissionArrangement key={spec.key} spec={spec} card={card}
                                      negotiatedPaths={negotiatedPaths} />;
      })}

      <p className="text-sm text-[#64748B] px-1">
        <strong className="text-[#1A1A1A]">You do not have to pick one now.</strong> A client
        runs under exactly one arrangement, and you can hold different ones across different
        clients — so the choice is made per client, as they come, not once for your whole book.
      </p>

      {!acceptAtTop && acceptBox}

      <p className="text-xs text-[#64748B] text-center">
        All rates ex-GST. Proposal {proposal.reference}.
      </p>
    </div>
  );
};

export const ProposalLoading = () => (
  <div className="flex items-center justify-center py-16">
    <Loader2 className="w-6 h-6 animate-spin text-[#64748B]" />
  </div>
);

export default ProposalContent;
